import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useApp } from '../lib/app-context';
import type { AuthResponse } from '../lib/types';
import { clearReferral, getReferral } from '../lib/referral';
import { language, t } from '../lib/i18n';

/**
 * "Continuer avec Google".
 *
 * Google renders the button itself inside our container — that is how the
 * official library works, and it is also why the button cannot be styled here:
 * Google requires its own mark and wording. The browser talks to Google, gets an
 * ID token back, and we hand that token to the API, which verifies it against
 * Google's public keys before trusting a single field in it.
 *
 * The button only appears when the server says a client id is configured, so a
 * deployment without one shows the email form alone rather than a button that
 * fails on click.
 */

type GoogleConfig = { google: { enabled: boolean; clientId: string | null } };

type CredentialResponse = { credential?: string };

type GoogleIdentityApi = {
  accounts: {
    id: {
      initialize: (options: {
        client_id: string;
        callback: (response: CredentialResponse) => void;
        auto_select?: boolean;
        cancel_on_tap_outside?: boolean;
      }) => void;
      renderButton: (
        parent: HTMLElement,
        options: Record<string, string | number | boolean>,
      ) => void;
    };
  };
};

declare global {
  interface Window {
    google?: GoogleIdentityApi;
  }
}

const SCRIPT_SRC = 'https://accounts.google.com/gsi/client';

/*
 * One fetch and one script tag for the whole app, however many times the button
 * is mounted — login and signup both render it, and React remounts on every
 * route change.
 */
let configPromise: Promise<GoogleConfig> | null = null;
const loadConfig = (): Promise<GoogleConfig> => {
  configPromise ??= api
    .get<GoogleConfig>('/auth/config', { anonymous: true })
    .catch(() => ({ google: { enabled: false, clientId: null } }));
  return configPromise;
};

let scriptPromise: Promise<boolean> | null = null;
const loadScript = (): Promise<boolean> => {
  scriptPromise ??= new Promise<boolean>((resolve) => {
    if (window.google?.accounts?.id) {
      resolve(true);
      return;
    }
    const script = document.createElement('script');
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve(Boolean(window.google?.accounts?.id));
    // Blocked script, offline, a tracker blocker: not an error worth shouting
    // about, the email form is right there.
    script.onerror = () => resolve(false);
    document.head.appendChild(script);
  });
  return scriptPromise;
};

export default function GoogleButton({
  label = 'signin_with',
  redirectTo = '/compte',
  divider = 'before',
}: {
  /** Google's own wording key: `signin_with` on login, `signup_with` on signup. */
  label?: 'signin_with' | 'signup_with' | 'continue_with';
  redirectTo?: string;
  /**
   * Where the "ou" goes. Below the form the button is the alternative, so the
   * divider comes first; at the top of signup the form is the alternative, and
   * an "ou" above the first option reads as if something came before it.
   */
  divider?: 'before' | 'after';
}) {
  const { signIn, toast, reportError } = useApp();
  const navigate = useNavigate();
  const container = useRef<HTMLDivElement>(null);
  const [ready, setReady] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const [busy, setBusy] = useState(false);

  // Kept in a ref so the callback Google holds always sees the current handler
  // rather than the one captured on the first render.
  const onCredential = useRef<(response: CredentialResponse) => void>(() => undefined);
  onCredential.current = (response) => {
    if (!response.credential) return;
    setBusy(true);
    void api
      .post<AuthResponse>(
        '/auth/google',
        // A partner code travels too; the server applies it only if this
        // sign-in creates the account.
        {
          idToken: response.credential,
          // Bedly's language, not the Google account's: it is what the person chose here.
          locale: language,
          ...(getReferral() ? { affiliateCode: getReferral() } : {}),
        },
        { anonymous: true },
      )
      .then((auth) => {
        clearReferral();
        signIn(auth);
        toast(t('Bienvenue, {name} !', { name: auth.user.fullName.split(' ')[0] ?? '' }), 'ok');
        // Google has already verified the address, and that is what booking
        // and publishing check — so they go straight where they were heading.
        navigate(redirectTo, { replace: true });
      })
      .catch(reportError)
      .finally(() => setBusy(false));
  };

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      const config = await loadConfig();
      if (cancelled) return;
      if (!config.google.enabled || !config.google.clientId) {
        setUnavailable(true);
        return;
      }

      const loaded = await loadScript();
      if (cancelled) return;
      if (!loaded || !window.google || !container.current) {
        setUnavailable(true);
        return;
      }

      window.google.accounts.id.initialize({
        client_id: config.google.clientId,
        callback: (response) => onCredential.current(response),
        // No One Tap prompt: it pops over the form uninvited. The button is the
        // whole interaction.
        auto_select: false,
        cancel_on_tap_outside: true,
      });
      window.google.accounts.id.renderButton(container.current, {
        type: 'standard',
        theme: 'outline',
        size: 'large',
        text: label,
        shape: 'pill',
        logo_alignment: 'left',
        locale: language,
        width: 320,
      });
      setReady(true);
    })();

    return () => {
      cancelled = true;
    };
  }, [label]);

  if (unavailable) return null;

  const or = (
    <div className="row row--between" style={{ gap: 10 }}>
      <hr style={{ flex: 1, border: 0, borderTop: '1px solid var(--line)' }} />
      <span className="tiny muted">ou</span>
      <hr style={{ flex: 1, border: 0, borderTop: '1px solid var(--line)' }} />
    </div>
  );

  return (
    <div className="stack stack--tight">
      {divider === 'before' ? or : null}
      <div
        ref={container}
        style={{
          display: 'flex',
          justifyContent: 'center',
          minHeight: 44,
          opacity: busy ? 0.5 : 1,
          pointerEvents: busy ? 'none' : 'auto',
        }}
      />
      {ready ? null : <p className="tiny muted center">{t("Chargement de Google…")}</p>}
      {divider === 'after' ? or : null}
    </div>
  );
}
